import React, { useState, useEffect, useRef } from 'react';
import { usersService } from '../services/users.service';
import { useAuth } from '../context/AuthContext';
import { User } from '../types';
import Header from '../components/Layout/Header';
import MobileSidebar from '../components/Layout/mobileSidebar';
import Button from '../components/Button';
import { isCoAdminOrAdmin, isAdminRole, formatRoleLabel } from '../utils/roles';
import './UserManagement.css';

const MOBILE_TABLET_BREAKPOINT = 1024; // px

const ROLE_OPTIONS = [
  'EMPLOYEE',
  'BRANCH_ADMIN',
  'DEPARTMENT_ADMIN',
  'CONTENT_UPLOADER',
  'CO_ADMIN',
  'ADMIN',
] as const;

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('ALL');
  const [filterRole, setFilterRole] = useState('ALL');
  const [isImporting, setIsImporting] = useState(false);
  const [showImportSection, setShowImportSection] = useState(false);
  const [importError, setImportError] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState<boolean>(
    () =>
      typeof window !== 'undefined'
        ? window.innerWidth <= MOBILE_TABLET_BREAKPOINT
        : true
  );

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteUserId, setPendingDeleteUserId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [phoneError, setPhoneError] = useState('');

  // Form state
  const [formData, setFormData] = useState<{
    empNo: string;
    fullName: string;
    department: string;
    branch: string;
    password: string;
    email: string;
    telNo: string;
    designation: string;
    role: User['role'];
  }>({
    empNo: '',
    fullName: '',
    department: '',
    branch: '',
    password: '',
    email: '',
    telNo: '',
    designation: '',
    role: 'EMPLOYEE',
  });

  const departments = ['IT', 'HR', 'ADMIN', 'FINANCE', 'SALES', 'SERVICE'];
  const branches = [
    'Ampara',
    'Anuradhapura',
    'BPC Wattala',
    'Galle',
    'Gampaha',
    'Hino Centre - Wattala',
    'Hyde Park Corner',
    'Kalutara',
    'Kandy',
    'Kurunegala',
    'Maharagama',
    'Matara',
    'Negombo',
    'Nugegoda',
    'Panchikawatte',
    'Ratmalana',
    'Ratnapura',
    'Toyota Plaza - Wattala'
  ];
  const roles = [...ROLE_OPTIONS];
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  // keep filterUsers stable and run when its inputs change
  const filterUsers = React.useCallback(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          u.fullName.toLowerCase().includes(term) ||
          u.empNo.toLowerCase().includes(term)
      );
    }

    // Department filter
    if (filterDepartment !== 'ALL') {
      filtered = filtered.filter((u) => u.department === filterDepartment);
    }

    // Role filter
    if (filterRole !== 'ALL') {
      filtered = filtered.filter((u) => u.role === filterRole);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterDepartment, filterRole]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  useEffect(() => {
    const onResize = () =>
      setIsMobileOrTablet(window.innerWidth <= MOBILE_TABLET_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const loadUsers = async () => {
    try {
      const data = await usersService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  

  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const isValidPhoneNumber = (phone: string) => {
    return /^(?:0\d{9}|94\d{9}|\+94\d{9})$/.test(phone);
  };

  const parseCSVLine = (line: string) => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
        continue;
      }

      if (char === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }

    values.push(current.trim());
    return values;
  };

  const handleImportClick = () => {
    setShowImportSection(true);
    window.setTimeout(() => importInputRef.current?.click(), 0);
  };

  const handleDownloadTemplate = async () => {
    setImportError('');
    try {
      await usersService.downloadImportTemplate();
    } catch (err: any) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to download CSV template.';
      setImportError(message);
    }
  };

  const handleImportCSV = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportError('');
    setIsImporting(true);

    try {
      // Refresh users to get the latest state before duplicate checks
      const latestUsers = await usersService.getAll();
      setUsers(latestUsers);

      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean);

      if (lines.length < 2) {
        throw new Error('CSV must contain a header row and at least one user row.');
      }

      const normalizeHeader = (h: string) => h.toLowerCase().replace(/[\s_]/g, '');
      const headersRaw = parseCSVLine(lines[0]);
      const headers = headersRaw.map(normalizeHeader);
      const headerIndex: Record<string, number> = {};
      headers.forEach((h, idx) => { headerIndex[h] = idx; });

      // Expected format (case/spacing insensitive):
      // empNo, fullName, department, branch, password, role, USER EMAIL, DESIGNATION, MOBILE
      const requiredHeaders = ['empno', 'fullname', 'department', 'branch', 'password', 'role'];
      const missingHeaders = requiredHeaders.filter((h) => headerIndex[h] === undefined);
      if (missingHeaders.length > 0) {
        throw new Error(
          `Missing required headers: ${missingHeaders.join(', ')}. Expected columns: Emp No, Full Name, Department, Branch, Password, Role. Optional: User Email, Designation, Mobile.`
        );
      }

      const getValue = (row: string[], key: string) => {
        const index = headerIndex[key];
        return index !== undefined ? row[index]?.trim() || '' : '';
      };

      const parsedUsers = lines.slice(1).map((line, idx) => {
        const columns = parseCSVLine(line);
        let role = getValue(columns, 'role').toUpperCase();
        if (role === 'HR_ADMIN') role = 'CO_ADMIN';
        const rawEmail = getValue(columns, 'useremail');
        const safeEmail = rawEmail && isValidEmail(rawEmail) ? rawEmail : '';
        const rawMobile = getValue(columns, 'mobile');

        if (!requiredHeaders.every((field) => getValue(columns, field))) {
          throw new Error(`Row ${idx + 2}: one or more required fields are empty.`);
        }

        if (!roles.includes(role as any)) {
          throw new Error(`Row ${idx + 2}: role must be one of ${roles.join(', ')}.`);
        }

        return {
          empNo: getValue(columns, 'empno'),
          fullName: getValue(columns, 'fullname'),
          department: getValue(columns, 'department'),
          branch: getValue(columns, 'branch'),
          password: getValue(columns, 'password'),
          email: safeEmail || undefined,
          telNo: rawMobile ?? '',
          designation: getValue(columns, 'designation') || '',
          role: role as User['role'],
        };
      });

      let importedCount = 0;
      let skippedDuplicates = 0;

      // Only enforce unique emails (backend still has unique on email). empNo is allowed to duplicate.
      const existingEmails = new Set(
        latestUsers
          .map((u) => u.email?.toLowerCase())
          .filter((e): e is string => !!e)
      );
      const seenEmails = new Set<string>();

      for (const userData of parsedUsers) {
        const emailKey = userData.email ? userData.email.toLowerCase() : '';

        // Skip if email is present and already exists (db or earlier in file)
        if (emailKey && (existingEmails.has(emailKey) || seenEmails.has(emailKey))) {
          skippedDuplicates++;
          continue;
        }

        try {
          await usersService.create(userData);
          importedCount++;
          if (emailKey) seenEmails.add(emailKey);
        } catch (err: any) {
          // Skip duplicate conflicts (email) and continue
          if (err?.response?.status === 409) {
            skippedDuplicates++;
            continue;
          }
          throw err;
        }
      }

      await loadUsers();
      const summary = [
        `Imported: ${importedCount}`,
        skippedDuplicates ? `Skipped duplicates: ${skippedDuplicates}` : null,
      ]
        .filter(Boolean)
        .join(' | ');
      setImportError(
        skippedDuplicates ? `Skipped ${skippedDuplicates} duplicate rows (email/empNo).` : ''
      );
      alert(summary || 'Import complete');
    } catch (err: any) {
      const message = err?.message || err?.response?.data?.message || 'CSV import failed.';
      setImportError(message);
    } finally {
      setIsImporting(false);
      // Allow uploading the same file again after an error/success
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      empNo: '',
      fullName: '',
      department: '',
      branch: '',
      password: '',
      email: '',
      telNo: '',
      designation: '',
      role: 'EMPLOYEE',
    });
    setError('');
    setPhoneError('');
    setShowModal(true);
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      empNo: user.empNo,
      fullName: user.fullName,
      department: user.department,
      branch: user.branch,
      password: '',
      email: user.email || '',
      telNo: user.telNo || '',
      designation: user.designation || '',
      role: user.role,
    });
    setError('');
    setPhoneError('');
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setPhoneError('');
    setIsSubmitting(true);

    const normalizedPhone = formData.telNo.trim();

    if (!editingUser && !normalizedPhone) {
      setPhoneError('Phone number is required.');
      setIsSubmitting(false);
      return;
    }

    if (normalizedPhone && !isValidPhoneNumber(normalizedPhone)) {
      setPhoneError('Phone number must be valid Sri Lankan format (e.g. 0742256408).');
      setIsSubmitting(false);
      return;
    }

    if (formData.email && !isValidEmail(formData.email)) {
      setError('Please enter a valid email address.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (editingUser) {
        const roleChanged = formData.role !== editingUser.role;
        if (roleChanged && !isAdminRole(currentUser?.role)) {
          setError('Only an administrator can change a user’s role. Ask an admin or revert the role.');
          setIsSubmitting(false);
          return;
        }

        const updateData: Parameters<typeof usersService.update>[1] = {
          empNo: formData.empNo,
          fullName: formData.fullName,
          department: formData.department,
          branch: formData.branch,
          email: formData.email || undefined,
          telNo: normalizedPhone,
          designation: formData.designation || '',
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        await usersService.update(editingUser.id, updateData);
        if (roleChanged && isAdminRole(currentUser?.role)) {
          await usersService.changeRole(editingUser.id, formData.role);
        }
      } else {
        // Create user
        await usersService.create({
          empNo: formData.empNo,
          fullName: formData.fullName,
          department: formData.department,
          branch: formData.branch,
          password: formData.password,
          email: formData.email || undefined,
          telNo: normalizedPhone,
          designation: formData.designation || '',
          role: formData.role,
        });
      }

      setShowModal(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = (userId: string) => {
    setPendingDeleteUserId(userId);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDeleteUserId) return;
    setIsDeleting(true);

    try {
      await usersService.delete(pendingDeleteUserId);
      setPendingDeleteUserId(null);
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      alert('Failed to delete user');
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleBadgeClass = (role: string) => {
    switch (role) {
      case 'ADMIN':
        return 'badge-admin';
      case 'CO_ADMIN':
        return 'badge-hr';
      case 'CONTENT_UPLOADER':
        return 'badge-content';
      case 'BRANCH_ADMIN':
      case 'DEPARTMENT_ADMIN':
        return 'badge-content';
      default:
        return 'badge-employee';
    }
  };

  if (!isCoAdminOrAdmin(currentUser?.role)) {
    return (
      <div className="user-management-page">
        <Header />
        <div className="access-denied">
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management-page">
      <Header />
      {/* Hamburger for mobile/tablet only */}
      {isMobileOrTablet && (
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          onClick={() => setMobileSidebarOpen(true)}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      )}
      <div className="user-management-container">
        <div className="page-header">
          <div className="header-info">
            <h1>User Management</h1>
            <p>Manage employee accounts and permissions</p>
          </div>
          <div className="header-actions">
            <button
              className="btn-import"
              onClick={handleImportClick}
              disabled={isImporting || isLoading}
            >
              {isImporting ? 'Importing...' : 'Import CSV'}
            </button>
            <Button className="btn-create" onClick={openCreateModal}>
              + Add User
            </Button>
          </div>
        </div>

        {showImportSection && (
          <div className="import-upload-section">
            <div className="import-upload-header">
              <strong>Import CSV</strong>
              <button
                type="button"
                className="btn-import"
                onClick={handleDownloadTemplate}
                disabled={isImporting || isLoading}
              >
                Download Template
              </button>
            </div>
            <input
              ref={importInputRef}
              type="file"
              accept=".csv"
              onChange={handleImportCSV}
              disabled={isImporting || isLoading}
            />

            <div className="import-csv-format">
              <h4 className="import-csv-format-title">CSV format</h4>
              <p className="import-csv-format-intro">
                Use UTF-8 encoding. The first row must be column headers. Column order can vary; names are
                matched case-insensitively (spaces and underscores are ignored). Use double quotes around a
                field if it contains commas.
              </p>
              <div className="import-csv-table-wrap">
                <table className="import-csv-table">
                  <thead>
                    <tr>
                      <th>Column</th>
                      <th>Required</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><code>Emp No</code></td>
                      <td>Yes</td>
                      <td>Employee number</td>
                    </tr>
                    <tr>
                      <td><code>Full Name</code></td>
                      <td>Yes</td>
                      <td>Display name</td>
                    </tr>
                    <tr>
                      <td><code>Department</code></td>
                      <td>Yes</td>
                      <td>e.g. IT, HR, SALES</td>
                    </tr>
                    <tr>
                      <td><code>Branch</code></td>
                      <td>Yes</td>
                      <td>Branch name (match your org list)</td>
                    </tr>
                    <tr>
                      <td><code>Password</code></td>
                      <td>Yes</td>
                      <td>Initial password (hashed on save)</td>
                    </tr>
                    <tr>
                      <td><code>Role</code></td>
                      <td>Yes</td>
                      <td>
                        {roles.join(', ')} — <code>HR_ADMIN</code> is accepted as <code>CO_ADMIN</code>
                      </td>
                    </tr>
                    <tr>
                      <td><code>User Email</code></td>
                      <td>No</td>
                      <td>Must be unique; invalid or duplicate emails are skipped</td>
                    </tr>
                    <tr>
                      <td><code>Designation</code></td>
                      <td>No</td>
                      <td>Job title</td>
                    </tr>
                    <tr>
                      <td><code>Mobile</code></td>
                      <td>No</td>
                      <td>Sri Lankan format (e.g. 0741234567)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="import-csv-example-label">Example (header + one row)</p>
              <pre className="import-csv-example">
{`Emp No,Full Name,Department,Branch,Password,Role,User Email,Designation,Mobile
E001,John Doe,IT,Kandy,TempPass123!,EMPLOYEE,john.doe@company.com,Engineer,0741234567`}
              </pre>
            </div>
          </div>
        )}

        {importError && <div className="import-error">{importError}</div>}

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by name or employee no..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <select
              value={filterDepartment}
              onChange={(e) => setFilterDepartment(e.target.value)}
            >
              <option value="ALL">All Departments</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>{dept}</option>
              ))}
            </select>
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
            >
              <option value="ALL">All Roles</option>
              {roles.map((role) => (
                <option key={role} value={role}>
                  {formatRoleLabel(role)}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="users-table-container">
          {isLoading ? (
            <div className="loading">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="no-users">No users found</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Branch</th>
                  <th>Role</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.profilePicture ? (
                            <img src={user.profilePicture} alt={user.fullName} />
                          ) : (
                            user.fullName.charAt(0)
                          )}
                        </div>
                        <div>
                          <div className="user-name">{user.fullName}</div>
                          <div className="user-empno">{user.empNo}</div>
                        </div>
                      </div>
                    </td>
                    <td>{user.department}</td>
                    <td>{user.branch}</td>
                    <td>
                      <span className={`role-badge ${getRoleBadgeClass(user.role)}`}>
                        {formatRoleLabel(user.role)}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-edit"
                          onClick={() => openEditModal(user)}
                        >
                          Edit
                        </button>
                        {isAdminRole(currentUser?.role) && user.id !== currentUser?.id && (
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(user.id)}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* User count */}
        <div className="table-footer">
          <span>Total: {filteredUsers.length} users</span>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Edit User' : 'Create User'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="error-message">{error}</div>}

                <div className="form-row">
                  <div className="form-group">
                    <label>Employee No *</label>
                    <input
                      type="text"
                      value={formData.empNo}
                      onChange={(e) => setFormData({ ...formData, empNo: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={formData.fullName}
                      onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Department *</label>
                    <select
                      value={formData.department}
                      onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                      required
                    >
                      <option value="">Select</option>
                      {departments.map((dept) => (
                        <option key={dept} value={dept}>{dept}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Branch *</label>
                    <select
                      value={formData.branch}
                      onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
                      required
                    >
                      <option value="">Select</option>
                      {branches.map((branch) => (
                        <option key={branch} value={branch}>{branch}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="name@example.com"
                  />
                  <div className="field-hint">Optional, not used for login</div>
                  </div>
                  <div className="form-group">
                    <label>Role *</label>
                    <select
                      value={formData.role}
                      onChange={(e) =>
                        setFormData({ ...formData, role: e.target.value as User['role'] })
                      }
                      required
                      disabled={!!editingUser && !isAdminRole(currentUser?.role)}
                      title={
                        editingUser && !isAdminRole(currentUser?.role)
                          ? 'Only administrators can change roles when editing'
                          : undefined
                      }
                    >
                      {roles.map((role) => (
                        <option key={role} value={role}>
                          {formatRoleLabel(role)}
                        </option>
                      ))}
                    </select>
                    {editingUser && !isAdminRole(currentUser?.role) && (
                      <div className="field-hint">
                        Only a full administrator can change another user’s role when editing.
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Phone{!editingUser ? ' *' : ''}</label>
                    <input
                      type="tel"
                      value={formData.telNo}
                      onChange={(e) => {
                        setFormData({ ...formData, telNo: e.target.value });
                        if (phoneError) setPhoneError('');
                      }}
                      required={!editingUser}
                      inputMode="numeric"
                      placeholder="0771234567"
                    />
                    {phoneError && <div className="field-error">{phoneError}</div>}
                  </div>

                  <div className="form-group">
                    <label>Designation</label>
                    <input
                      type="text"
                      value={formData.designation}
                      onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                      placeholder="Senior Executive"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{editingUser ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    placeholder={editingUser ? 'Leave blank to keep current' : 'Min 6 characters'}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : (editingUser ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {pendingDeleteUserId && (
        <div className="modal-overlay" onClick={() => !isDeleting && setPendingDeleteUserId(null)}>
          <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirm Delete</h2>
            </div>
            <div className="modal-body">
              <p className="delete-confirm-message">Are you sure you want to delete this user?</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setPendingDeleteUserId(null)}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-submit"
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isMobileOrTablet && mobileSidebarOpen && (
        <MobileSidebar onClose={() => setMobileSidebarOpen(false)} />
      )}
    </div>
  );
};

export default UserManagement;
